import { useEffect, useRef, useState } from 'react';
import { normalizeHex } from '@/lib/studio-color';
import { parseColorInput } from '@/lib/color-input';

interface ColorFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}

export function ColorField({ id, label, value, onChange, hint }: ColorFieldProps) {
  const cancelled = useRef(false);
  const [draft, setDraft] = useState(value);
  const parsed = parseColorInput(draft);
  const valid = parsed !== null;
  const descriptionId = !valid ? `${id}-error` : hint ? `${id}-hint` : undefined;

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      setDraft(value);
      return;
    }
    if (parsed) {
      const normalized = parsed.hex;
      setDraft(normalized);
      onChange(normalized);
    } else {
      setDraft(value);
    }
  };

  return (
    <div className="studio-field">
      <label htmlFor={id} className="studio-label">
        {label}
      </label>
      <div className="color-field-group">
        <input
          className="color-field-picker"
          type="color"
          value={normalizeHex(value)}
          onChange={(event) => onChange(normalizeHex(event.target.value))}
          aria-label={`${label} color picker`}
        />
        <input
          id={id}
          className="studio-input color-field-text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              cancelled.current = true;
              setDraft(value);
              event.currentTarget.blur();
            }
          }}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={!valid}
          aria-describedby={descriptionId}
        />
      </div>
      {!valid ? (
        <span id={`${id}-error`} className="studio-field-error" role="alert">
          Use a 3 or 6 digit HEX value, RGB, HSL, OKLab, or OKLCH. Colors must be opaque.
        </span>
      ) : parsed?.mapped ? (
        <span className="studio-field-hint">Chroma will be reduced to fit sRGB.</span>
      ) : hint ? (
        <span id={`${id}-hint`} className="studio-field-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
